import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { isEmail } from "@/lib/format";

export const COMMITMENTS = [
  "I will match every gift up to $500",
  "I will match every gift up to a set amount",
  "I will match the total raised",
  "I will match gifts made on a specific day",
];

const EMPTY = { name: "", email: "", phone: "", commitment: "", custom_amount: "" };

function validate(f) {
  const e = {};
  if (!f.name.trim()) e.name = "Please enter your name.";
  if (!isEmail(f.email)) e.email = "Please enter a valid email address.";
  if (!f.commitment) e.commitment = "Please choose one option.";
  if (f.custom_amount.trim() && !/^\$?\d[\d,]*(\.\d{1,2})?$/.test(f.custom_amount.trim())) e.custom_amount = "Please enter a number, like 1000.";
  return e;
}

const Field = ({ id, label, error, children }) => (
  <div>
    <label htmlFor={id} className="of-label">{label}</label>
    {children}
    {error && <p id={`${id}-error`} className="of-error" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

export function MatchForm() {
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setErrors({ ...errors, [k]: undefined }); };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("busy");
    try {
      const amt = f.custom_amount.replace(/[$,]/g, "").trim();
      await api.post("/matching-offers", { ...f, custom_amount: amt ? Number(amt) : null });
      setStatus("done");
    } catch (err) {
      setStatus(err?.response?.status === 422 ? "idle" : "error");
      if (err?.response?.status === 422) setErrors({ email: "Please enter a valid email address." });
    }
  };

  if (status === "done") {
    return (
      <p role="status" className="flex items-start gap-3 rounded-2xl bg-[#E8F3EC] p-5 text-lg font-medium text-[#1E5B3D]" data-testid="match-form-success">
        <CheckCircle2 className="mt-1 h-5 w-5 shrink-0" aria-hidden /> Thank you. Enza will reach out to you personally.
      </p>
    );
  }

  const inv = (k) => ({ "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `match-${k}-error` : undefined });
  return (
    <form onSubmit={submit} noValidate className="grid gap-5" data-testid="match-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="match-name" label="Your name" error={errors.name}>
          <input id="match-name" autoComplete="name" className="of-input" value={f.name} onChange={set("name")} {...inv("name")} data-testid="match-name-input" />
        </Field>
        <Field id="match-email" label="Email" error={errors.email}>
          <input id="match-email" type="email" autoComplete="email" className="of-input" value={f.email} onChange={set("email")} {...inv("email")} data-testid="match-email-input" />
        </Field>
        <Field id="match-phone" label="Phone (optional)">
          <input id="match-phone" type="tel" autoComplete="tel" className="of-input" value={f.phone} onChange={set("phone")} data-testid="match-phone-input" />
        </Field>
        <Field id="match-custom_amount" label="Custom amount (optional)" error={errors.custom_amount}>
          <input id="match-custom_amount" inputMode="decimal" className="of-input" value={f.custom_amount} onChange={set("custom_amount")} placeholder="$" {...inv("custom_amount")} data-testid="match-custom-amount-input" />
        </Field>
      </div>
      <fieldset aria-describedby={errors.commitment ? "match-commitment-error" : undefined}>
        <legend className="of-label">How would you like to match?</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {COMMITMENTS.map((c, i) => (
            <label key={c} className={`flex min-h-[52px] cursor-pointer items-center gap-3 rounded-2xl border-2 bg-white px-4 py-3 transition-colors ${
              f.commitment === c ? "border-[#24453D] bg-[var(--pg-soft)]" : "border-[#D9CFC3] hover:border-[#24453D]/60"}`}>
              <input type="radio" name="commitment" value={c} checked={f.commitment === c} onChange={set("commitment")}
                className="h-5 w-5 accent-[#24453D]" data-testid={`match-commitment-option-${i + 1}`} />
              <span>{c}</span>
            </label>
          ))}
        </div>
        {errors.commitment && <p id="match-commitment-error" className="of-error" data-testid="match-commitment-error">{errors.commitment}</p>}
      </fieldset>
      {status === "error" && <p role="alert" className="of-error">We couldn't send this just now. Please try again, or call (347) 277-2133.</p>}
      <div>
        <button type="submit" className="of-btn-soft" disabled={status === "busy"} data-testid="match-form-submit-button">
          {status === "busy" ? "Sending…" : "Offer a match"}
        </button>
        <p className="mt-2 text-sm text-[#4A5160]">This is only an offer. Nothing is charged. Enza will talk it through with you.</p>
      </div>
    </form>
  );
}
